/**
 *
 * ES6 Accessible modal class
 *
 **/

import A11yUtils from '@core/base/scripts/utils/a11y-utils.js';

const SPEED = 100;
const CLASSES = {
  show: 'base-modal--show',
  opened: 'base-modal--opened',
  current: 'base-modal--current',
  scrollDisabled:'scroll-disabled'
};
const EVENTS = {
  show: 'base-modal:show',
  opened: 'base-modal:opened',
  closed: 'base-modal:closed'
};


export default class BaseModal {

  constructor() {
    this._btnClose=null;
    this._focusOnClose=null;
    this._focusOnOpen=null;
    this._currentModal=null;
    this._innerFocusables=null;
    this._bodyElt=null;
    this._module=null;
    this._modalId=null;
    this.isInit=false;
  }

  init(id) {
    this.isInit=true;
    this.trapFocusFunc = null;
    this.bodyElt = document.querySelector("body");

    // 1. set modal id and currentModal
    this.setModalId(id);
    this.currentModal = document.getElementById(this.getModalId());
    // 1. check if there are elements with data-modal atribute on page
    this.focusOnClose = document.querySelector('[data-modal=' + this.getModalId() + ']');
    // 2. init open event if 1. is true
    if (this.focusOnClose) {
      this.focusOnClose.addEventListener('click', (event) => {
        event.preventDefault();
        // open the modal
        this.open();
      });
    }
  }


  initCloseEvent() {
    const len = this.btnClose.length;
    let btn;
    for (let i = 0; i < len; i++) {
      btn = this.btnClose[i]
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.close();
        return false;
      });
    }
  }

  open() {
    try {
      if (!this.getModalId()) {
        throw 'Modal id was not found : please set within "open(id)" method';
      }
      if (!this.currentModal) {
        throw 'Modal with id "' + this.getModalId() + '" was not found : please check if you set an id to your base-modal.html.twig instance';
      }
    } catch (error) {
      console.error(error);
    }
    /*
     * close event listner
     */
    this.btnClose = this.currentModal.querySelectorAll('[data-modal-close]');
    this.initCloseEvent();
    /*
     * add CSS classes for animations and other stuff
     */
    this.addClass(this.currentModal, CLASSES.current);
    this.addClass(this.currentModal, CLASSES.show);
    /*
     * create an array with all the focusables from the modal itself
     */
    this.setInnerFocusables();
    /*
     * events listeners,
     * note: store keyDownEvents func in a new var, because bind() will change the method signature and removeEventListener will not work
     * https://stackoverflow.com/questions/10444077/javascript-removeeventlistener-not-working
     */
    this.trapFocusFunc = this.keyDownEvents.bind(this);
    this.currentModal.addEventListener("keydown", this.trapFocusFunc);
    /*
     * listen to a close event that can be trigger by any components in the website
     */
    this.currentModal.addEventListener('close', () => {
      this.close(true);
    });
    document.addEventListener('updateModalFocusables', () => {
      this.setInnerFocusables();
    });
    /*
     * wait for the opening modal CSS animation to finish and then do the focus
     */
    setTimeout(() => {
      this.manageFocus();
      this.addClass(this.currentModal, CLASSES.opened);
      this.addClass(this.bodyElt, CLASSES.scrollDisabled);
    }, this.speed);
    /*
     * Dispatch the event 'show'
     */
    this.triggerEvent(EVENTS.opened);
  }

  close(doNotFocus) {
    /*
     * remove CSS classes for animations and other stuff
     */
    this.removeClass(this.currentModal, CLASSES.current);
    this.removeClass(this.currentModal, CLASSES.opened);
    /*
     * do the focus on the button that opened the modal
     */
    if (!doNotFocus && this.focusOnClose) {
      this.focusOnClose.focus();
    }
    /*
     * wait for the closing modal CSS animation to finish and then do the focus
     */
    setTimeout(() => {
      this.removeClass(this.currentModal, CLASSES.show);
      this.removeClass(this.bodyElt, CLASSES.scrollDisabled);
      this.destroy();
      // Dispatch the event 'close'
      this.triggerEvent(EVENTS.closed);
    }, this.speed);
  }

  manageFocus() {
    /*
     * if focusOnOpen have been set, focus on it
     * else, if we have less than 1 focusables, focus on the first element (the close button) else focus on the second
     */
    const len = this.innerFocusables.length;
    if (!this.focusOnOpen) {
      if (len > 1) {
        this.innerFocusables[1].focus();
      } else {
        this.innerFocusables[0].focus();
      }
    } else {
      this.focusOnOpen.focus();
    }
  }

  setInnerFocusables() {
    /*
     * check if focusable is hidden or display none, if not, store it
     */
    this.innerFocusables = [].slice.call(
      this.currentModal.querySelectorAll(A11yUtils.FOCUSABLES)
    ).filter(el => !A11yUtils.isHidden(el));
  }

  keyDownEvents(evt) {
    evt = evt || window.event;
    /*
     * tab or Maj Tab in elt => capture focus
     */
    if (evt.keyCode === A11yUtils.keyboard.tab && this.innerFocusables.indexOf(evt.target) >= 0) {
      /*
       *maj-tab on first element focusable => focus on last
       */
      if (evt.shiftKey) {
        if (evt.target === this.innerFocusables[0]) {
          this.innerFocusables[this.innerFocusables.length - 1].focus();
          evt.preventDefault();
        }
      } else {
        /*
         * tab on last element focusable => focus on first
         */
        if (evt.target === this.innerFocusables[this.innerFocusables.length - 1]) {
          this.innerFocusables[0].focus();
          evt.preventDefault();
        }
      }
    }

    /*
     *  tab outside elt => put it in focus
     */
    if (evt.keyCode === A11yUtils.keyboard.tab && this.innerFocusables.indexOf(evt.target) === -1) {
      evt.preventDefault();
      this.innerFocusables[0].focus();
    }

    if (evt.keyCode === A11yUtils.keyboard.esc) {
      evt.preventDefault();
      this.close();
    }
  }

  onEvent(evt, callback) {
    document.addEventListener(evt, () => {
      if (callback) {
        callback();
      }
    });
  }

  destroy() {
    this.isInit=false;
    /*
     * remove all listener
     */
    this.currentModal.removeEventListener("keydown", this.trapFocusFunc);
    this.currentModal.removeEventListener('close', function () {});
    this.currentModal.removeEventListener('updateModalFocusables', function () {});
    // Destroy objects
    this.btnClose = null;
    this.innerFocusables = null;
  }


  ///////////////////////////////////////////////////////////////////////////
  // Getter/setter
  ///////////////////////////////////////////////////////////////////////////
  setModalId(id) {
    this._modalId = id;
  }

  getModalId() {
    return this._modalId;
  }

  set module(elt) {
    this._module = elt;
  }

  get module() {
    return this._module;
  }


  set btnClose(elt) {
    this._btnClose = elt;
  }

  get btnClose() {
    return this._btnClose;
  }

  set currentModal(elt) {
    this._currentModal = elt;
  }

  get currentModal() {
    return this._currentModal;
  }

  set focusOnClose(elt) {
    this._focusOnClose = elt;
  }

  get focusOnClose() {
    return this._focusOnClose;
  }

  set focusOnOpen(elt) {
    this._focusOnOpen = elt;
  }

  get focusOnOpen() {
    return this._focusOnOpen;
  }

  set innerFocusables(elts) {
    this._innerFocusables = elts;
  }

  get innerFocusables() {
    return this._innerFocusables;
  }

  set bodyElt(elt) {
    this._bodyElt = elt;
  }

  get bodyElt() {
    return this._bodyElt;
  }

  get speed(){
    return SPEED;
  }

  ///////////////////////////////////////////////////////////////////////////
  // UTILS, some 'jQuery like' methods
  ///////////////////////////////////////////////////////////////////////////
  addClass(el, className) {
    if (el.classList) {
      el.classList.add(className);
    } else {
      el.className += ' ' + className;
    }
  }

  removeClass(el, className) {
    if (el.classList) {
      el.classList.remove(className);
    } else {
      el.className = el.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }
  }

  hasClass(el, className) {
    if (el.classList) {
      return el.classList.contains(className);
    } else {
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(el.className);
    }
  }

  triggerEvent(evt) {
    var event = document.createEvent('HTMLEvents');
    event.initEvent(evt, true, false);
    document.dispatchEvent(event);
  }

}
